import { type Time } from "@internationalized/date";
import { safeParseTime as safeParseTimeShared } from "@/utils/date-time-utils";

export type TimeOffScope = "ONLY_THIS" | "THIS_AND_FOLLOWING";

/**
 * `OTHER` is the client's catch-all time-bounded type: seeded for everyone, no
 * default duration, and the one a block that fits nothing else goes under. It
 * is what "custom" means here — it leads the picker, and it is the only type
 * that asks the user about online booking rather than carrying an answer.
 */
export function isCustomTimeOffType(type: { systemKey: string | null }) {
  return type.systemKey === "OTHER";
}

/**
 * Custom first, then the client's own order. Sorting here rather than relying
 * on `sortOrder` keeps the lead card stable: a client can reorder their types,
 * and a seeded `OTHER` sits behind any custom type they place above it.
 */
export function sortPickableTimeOffTypes<
  T extends { systemKey: string | null },
>(types: T[]): T[] {
  return [...types].sort(
    (a, b) => Number(isCustomTimeOffType(b)) - Number(isCustomTimeOffType(a)),
  );
}

export function minuteOfDay(time: string) {
  const [hour, minute] = time.split(":");
  return Number(hour) * 60 + Number(minute);
}

export function durationBetween(startTime: string, endTime: string) {
  return minuteOfDay(endTime) - minuteOfDay(startTime);
}

export function safeParseTime(value: string): Time | null {
  return safeParseTimeShared(value, "timeOffForm.safeParseTime");
}

/** "" for a start that is not a time yet; clamped at 23:59, since the endpoint takes an intra-day window only. */
export function addMinutes(time: string, minutes: number) {
  const start = minuteOfDay(time);
  if (!Number.isFinite(start)) return "";
  const total = Math.min(start + minutes, 23 * 60 + 59);
  const hour = String(Math.floor(total / 60)).padStart(2, "0");
  const minute = String(total % 60).padStart(2, "0");
  return `${hour}:${minute}`;
}

/**
 * The wall-clock time a vertical offset inside a day column points at, snapped
 * to five minutes the way the calendar's own drag does. Shared by the block
 * that shows the time while a drag is in flight and by the drop that writes it,
 * so the label can never promise a slot the save would not use.
 */
export function timeAtColumnOffset({
  dayStartTime,
  yOffset,
  slotHeight,
  timeStep,
}: {
  dayStartTime: string;
  yOffset: number;
  slotHeight: number;
  timeStep: number;
}) {
  const fromTop = Math.max(yOffset, 0);
  const minutes = minuteOfDay(dayStartTime) + (fromTop / slotHeight) * timeStep;
  const snapped = Math.round(minutes / 5) * 5;
  // A block dragged past the end of the day still has to name a real time.
  const clamped = Math.min(Math.max(snapped, 0), 23 * 60 + 59);

  const hour = String(Math.floor(clamped / 60)).padStart(2, "0");
  const minute = String(clamped % 60).padStart(2, "0");
  return `${hour}:${minute}`;
}

/** What a drop on the calendar resolved to, before any scope is known. */
export type TimeOffDrop = {
  id: number;
  source: "staff" | "resource";
  seriesId: number | null;
  /** The occurrence's own day and target, to tell a real move from a nudge. */
  originDate: string;
  originUserId: number | null;
  originResourceId: number | null;
  date: string;
  startTime: string;
  userId: number | null;
  resourceId: number | null;
};

/**
 * A drag can only ever detach once it leaves the occurrence's own day or its
 * target: a series regenerates its dates from its rule and keeps whoever it was
 * created for, so the endpoint refuses either for "this and all following".
 * Lives here rather than beside the drag hook so it stays importable without
 * pulling the api client, and testable on its own.
 */
export function movesOffSeries(drop: TimeOffDrop) {
  return (
    drop.date !== drop.originDate ||
    (drop.userId != null && drop.userId !== drop.originUserId) ||
    (drop.resourceId != null && drop.resourceId !== drop.originResourceId)
  );
}

export type TimeOffMoveChanges = {
  startMinuteOfDay: number;
  date?: string;
  userId?: number;
  resourceId?: number;
};

/**
 * The changes a dragged occurrence sends.
 *
 * `date` and the target travel with ONLY_THIS and nothing else. A series
 * rewrite regenerates its dates from its rule and keeps whoever it was created
 * for, so the endpoint refuses all three outright — not only when they changed.
 * Sending an unchanged `date` with THIS_AND_FOLLOWING is still a refusal, which
 * is every same-day drop, so the gate is the scope rather than a comparison
 * against the origin.
 */
export function buildTimeOffMoveChanges(
  drop: {
    source: "staff" | "resource";
    date: string;
    startTime: string;
    userId: number | null;
    resourceId: number | null;
  },
  scope: TimeOffScope,
): TimeOffMoveChanges {
  const changes: TimeOffMoveChanges = {
    startMinuteOfDay: minuteOfDay(drop.startTime),
  };

  if (scope !== "ONLY_THIS") return changes;

  changes.date = drop.date;
  if (drop.source === "staff" && drop.userId != null) {
    changes.userId = drop.userId;
  }
  if (drop.source === "resource" && drop.resourceId != null) {
    changes.resourceId = drop.resourceId;
  }

  return changes;
}

export type DailyLimitRow = {
  id: number;
  source: "staff" | "resource";
  userId: number | null;
  typeId: number | null;
  timeFrom: string;
  timeTo: string;
};

/**
 * The first selected team member the form would put over the type's daily cap
 * on `date`, judged as the server judges it: only a change that makes the day
 * worse is refused. It sees the form's own day only, so the server still
 * decides the other days of a series.
 */
export function findDailyLimitBreach({
  type,
  rows,
  userIds,
  date,
  durationMins,
  editingId,
}: {
  type: { id: number; maxDailyMinutes: number | null };
  rows: DailyLimitRow[];
  userIds: number[];
  date: string;
  durationMins: number;
  /** The staff occurrence being edited, whose old window the edit replaces. */
  editingId: number | null;
}): { userId: number; totalMinutes: number } | null {
  const cap = type.maxDailyMinutes;
  if (cap == null) return null;

  for (const userId of userIds) {
    const ofType = rows.filter(
      (row) =>
        row.userId === userId &&
        row.source === "staff" &&
        row.typeId === type.id,
    );
    const before = sumMinutesOnDate(ofType, date);
    const after =
      sumMinutesOnDate(
        ofType.filter(
          (row) => !(row.source === "staff" && row.id === editingId),
        ),
        date,
      ) + durationMins;

    if (after > cap && after > before) return { userId, totalMinutes: after };
  }

  return null;
}

function sumMinutesOnDate(rows: DailyLimitRow[], date: string) {
  return rows.reduce((total, row) => total + minutesOnDate(row, date), 0);
}

/** A row's minutes inside `date`, from the location-local times the read returns. */
function minutesOnDate(row: DailyLimitRow, date: string) {
  const [fromDate = "", fromTime = "00:00"] = row.timeFrom.split("T");
  const [toDate = "", toTime = "00:00"] = row.timeTo.split("T");
  if (fromDate > date || toDate < date) return 0;

  const start = fromDate < date ? 0 : minuteOfDay(fromTime);
  const end = toDate > date ? 24 * 60 : minuteOfDay(toTime);
  return Math.max(end - start, 0);
}
